import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, Play } from "lucide-react";
import { WorkflowLaunchWizard } from "./WorkflowLaunchWizard";
import { ContributingAssetsWizard } from "./contributing-assets/ContributingAssetsWizard";
import { VimeoEmbed } from "@/components/ui/vimeo-embed";
import * as Icons from "lucide-react";

interface WorkflowDetailModalProps {
  workflow: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WorkflowDetailModal = ({
  workflow,
  open,
  onOpenChange,
}: WorkflowDetailModalProps) => {
  const [showLaunchWizard, setShowLaunchWizard] = useState(false);

  const getIconComponent = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons] as any;
    return Icon ? <Icon className="h-8 w-8" /> : <Icons.FileText className="h-8 w-8" />;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return "bg-green-500/10 text-green-500";
      case "Medium":
        return "bg-yellow-500/10 text-yellow-500";
      case "Hard":
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (showLaunchWizard) {
    // Use specialized wizard for asset transfers
    if (workflow.workflow_key === 'asset_transfers_contributions') {
      return (
        <ContributingAssetsWizard
          workflow={workflow}
          open={open}
          onOpenChange={(open) => {
            setShowLaunchWizard(false);
            onOpenChange(open);
          }}
        />
      );
    }
    
    // Use generic wizard for all other workflows
    return (
      <WorkflowLaunchWizard
        workflow={workflow}
        open={open}
        onOpenChange={(open) => {
          setShowLaunchWizard(false);
          onOpenChange(open);
        }}
      />
    );
  }

  const phases = Array.isArray(workflow.phases) ? workflow.phases : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/10">
              {getIconComponent(workflow.icon_name)}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl">{workflow.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={getDifficultyColor(workflow.difficulty)}>
                  {workflow.difficulty}
                </Badge>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {workflow.estimated_time}
                </Badge>
                <Badge variant="outline">
                  {phases.length} phases
                </Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Training Video */}
            {workflow.training_video_urls && workflow.training_video_urls.length > 0 && (
              <div className="mb-4">
                <VimeoEmbed url={workflow.training_video_urls[0]} title={`${workflow.title} Training Video`} />
              </div>
            )}

            {/* Phase Structure */}
            {phases.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Workflow Phases</h3>
                <div className="space-y-4">
                  {phases.map((phase: any, index: number) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Phase {phase.phase_number}</Badge>
                        <h4 className="font-medium">{phase.phase_name}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Guidance Notes */}
            {workflow.guidance_notes && (
              <>
                <Separator />
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-semibold mb-2">Guidance Notes</h3>
                  <p className="text-sm text-muted-foreground">{workflow.guidance_notes}</p>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-3 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={() => setShowLaunchWizard(true)} className="gap-2">
            <Play className="h-4 w-4" />
            Start This Workflow
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
